import { ref } from "vue";
import { defineStore } from "pinia";
import { authApi } from "@/plugins/axios";
import type { CustomAxiosRequestConfig } from "@/plugins/axios";
import type {
  DailyReceiptCount,
  WeeklyReceiptCount,
  AgencyReceiptCount,
} from "@/types/dashboard";
import type { Receipt } from "@/types/receipt";
import type { ApiResult } from "@/types/auth";

const API_ENDPOINTS = {
  DAILY_TOTALS: "/api/platformreceipts/today-totals",
  WEEKLY_TOTALS: "/api/platformreceipts/week-totals",
  AGENCY_TOTALS: "/api/platformreceipts/agency-receipts-count",
  LATEST_RECEIPTS: "/api/platformreceipts/receipts?PageSize=20",
} as const;

export const useDashboardStore = defineStore("dashboard", () => {
  const dailyReceiptCount = ref<DailyReceiptCount | null>(null);
  const weeklyReceiptCount = ref<WeeklyReceiptCount | null>(null);
  const agencyReceiptCount = ref<AgencyReceiptCount[]>([]);
  const latestReceipts = ref<Receipt[]>([]);

  const error = ref<Error | null>(null);

  const resetDashboard = () => {
    dailyReceiptCount.value = null;
    weeklyReceiptCount.value = null;
    agencyReceiptCount.value = [];
    latestReceipts.value = []
    error.value = null;
  };

    const dailyCount = async (): Promise<ApiResult> => {
    resetDashboard()

    try {
      error.value = null;
      const response = await authApi.get(API_ENDPOINTS.DAILY_TOTALS, {
            showLoader: true,
          } as CustomAxiosRequestConfig)

      if (response.status === 200) {
        dailyReceiptCount.value = response.data;
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      error.value =
        err instanceof Error ? err : new Error("Failed to load daily summary.");
      console.log(err);

      return { success: false };
    }
  };

  const dashboardSummary = async (): Promise<void> => {
    try {
      const [weeklyResponse, agencyResponse, latestResponse] =
        await Promise.all([
          authApi.get(API_ENDPOINTS.WEEKLY_TOTALS, {
            showLoader: true,
          } as CustomAxiosRequestConfig),
          authApi.get(API_ENDPOINTS.AGENCY_TOTALS, {
            showLoader: true,
          } as CustomAxiosRequestConfig),
          authApi.get(API_ENDPOINTS.LATEST_RECEIPTS, {
            showLoader: true,
          } as CustomAxiosRequestConfig),
        ]);

      weeklyReceiptCount.value = weeklyResponse.data;
      agencyReceiptCount.value = agencyResponse.data;
      latestReceipts.value = latestResponse.data;
      if (weeklyReceiptCount.value) {
        weeklyReceiptCount.value.selectedDate =
          weeklyReceiptCount.value.selectedDate.map((d) => {
            const [m, d2, y] = d.split("/");
            return `${d2}/${m}/${y}`;
          });
      }
    } catch (err) {
      error.value =
        err instanceof Error
          ? err
          : new Error(
              "Failed to load daily summary."
            );
    }
  };

  return {
    dailyReceiptCount,
    weeklyReceiptCount,
    agencyReceiptCount,
    latestReceipts,
    error,
    dashboardSummary,
    resetDashboard,
    dailyCount
  };
});